import { useState, useEffect, useRef, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { SignupForm, type SignupFormData } from "@/components/auth/SignupForm";
import { trackSignupConversion } from "@/utils/analytics";
import { buildSignupVerifiedPayload } from "@/utils/marketing";
import { getRandomAvatar } from "@/lib/avatarUtils";
import { saveProfileSnapshot } from "@/lib/profileSnapshot";
import { preloadAvatar } from "@/lib/avatarPreloader";
import { useQueryClient } from "@tanstack/react-query";
import { useBookAvatars } from "@/hooks/use-book-avatars";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { MediaUnlocker } from "@/lib/mediaUnlocker";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess?: () => void;
  onTransitionToProfileSelection?: () => void;
  context?: string;
}

interface StoredSignupData {
  firstName: string;
  lastName: string;
  country: string;
  avatarUrl?: string;
}

export function AuthModal({ isOpen, onClose, onAuthSuccess, onTransitionToProfileSelection, context }: AuthModalProps) {
  const [step, setStep] = useState<'email' | 'signup' | 'avatar' | 'otp'>('email');
  const [email, setEmail] = useState(() => sessionStorage.getItem('auth_modal_email') || '');
  const [otp, setOtp] = useState(() => sessionStorage.getItem('auth_modal_otp') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [signupData, setSignupData] = useState<StoredSignupData | null>(null);
  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState<number | null>(null);
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const otpInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { avatars, isLoading: avatarsLoading } = useBookAvatars();

  // Get shuffled avatars for display
  const displayAvatars = useMemo(() => {
    if (!avatars || avatars.length === 0) return [];
    const shuffled = [...avatars].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 20);
  }, [avatars]);

  // Trigger haptic feedback on avatar selection
  const triggerHaptic = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch {
        // Haptics not available
      }
    }
  };

  const handleAvatarSelect = (index: number, url: string) => {
    triggerHaptic();
    setSelectedAvatarIndex(index);
    setSelectedAvatarUrl(url);
  };

  // Persist email and OTP to sessionStorage
  useEffect(() => {
    if (email) {
      sessionStorage.setItem('auth_modal_email', email);
    }
  }, [email]);

  useEffect(() => {
    if (otp) {
      sessionStorage.setItem('auth_modal_otp', otp);
    }
  }, [otp]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Restore from sessionStorage if available, otherwise reset
      const savedEmail = sessionStorage.getItem('auth_modal_email');
      const savedOtp = sessionStorage.getItem('auth_modal_otp');
      const savedStep = sessionStorage.getItem('auth_modal_step') as 'email' | 'otp' | null;
      
      setEmail(savedEmail || '');
      setOtp(savedOtp || '');
      setStep(savedStep || 'email');
      setResendCooldown(0);
    } else {
      // Clear sessionStorage when modal closes successfully
      sessionStorage.removeItem('auth_modal_email');
      sessionStorage.removeItem('auth_modal_otp');
      sessionStorage.removeItem('auth_modal_step');
    }
  }, [isOpen]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus OTP input when step changes to 'otp'
  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
    }
  }, [step]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    
    try {
      // Check if user exists by querying auth.users via a simple sign-in attempt
      const { data: { user }, error: checkError } = await supabase.auth.getUser();
      
      // Try to sign in with shouldCreateUser: false to check if user exists
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: false,
        }
      });

      // Check for any error that indicates user doesn't exist
      if (signInError) {
        const msg = String(signInError.message || '').toLowerCase();
        console.log('SignIn error:', msg);
        
        // Any error with shouldCreateUser: false means user doesn't exist or signup needed
        // Redirect to signup form
        setIsNewUser(true);
        setStep('signup');
        sessionStorage.setItem('auth_modal_step', 'signup');
        toast({
          title: 'Create account',
          description: 'Enter your details to continue.',
        });
        setIsLoading(false);
        return;
      }

      // OTP sent for existing user — proceed to verification
      setIsNewUser(false);
      setStep('otp');
      sessionStorage.setItem('auth_modal_step', 'otp');
      setResendCooldown(60);
      toast({
        title: 'Check your email',
        description: "We've sent a 6-digit code to your email address."
      });
    } catch (error: any) {
      console.error('Email submit error:', error);
      toast({
        title: 'Error',
        description: 'Failed to send verification code. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return;

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email'
      });

      if (error) {
        toast({
          title: "Invalid code",
          description: "Please check the code and try again.",
          variant: "destructive"
        });
      } else {
        // If this is a new user signup (went through signup form), create profile and notify Zapier
        if (isNewUser && data.user && signupData) {
          trackSignupConversion({
            user_id: data.user.id,
            email: email,
            country: signupData.country || 'unknown',
            signup_method: 'email_otp',
            timestamp: new Date().toISOString()
          });
          
          // Notify Zapier of signup_verified (fire and forget)
          const marketingPayload = buildSignupVerifiedPayload({
            user_id: data.user.id,
            email: email,
            first_name: signupData.firstName || '',
            country: signupData.country || 'unknown'
          });
          
          supabase.functions.invoke('notify-signup', {
            body: marketingPayload
          }).catch(err => console.log('[AuthModal] signup_verified webhook failed:', err));
          
          // CRITICAL: Create or update the primary profile with collected data and random avatar
          try {
            const fullName = `${signupData.firstName} ${signupData.lastName}`.trim();

            // Wait a moment for the database trigger to create the profile first
            await new Promise(resolve => setTimeout(resolve, 500));

            // Find existing primary profile with retry (trigger may take a moment)
            let existingPrimary: { id: string; avatar_url: string | null } | null = null;
            for (let attempt = 0; attempt < 3; attempt++) {
              const { data: found, error: existingError } = await supabase
                .from('user_profiles')
                .select('id, avatar_url')
                .eq('user_id', data.user.id)
                .eq('is_primary', true)
                .maybeSingle();

              if (existingError) {
                console.error('[AuthModal] Error checking primary profile:', existingError);
              }
              
              if (found) {
                existingPrimary = found;
                break;
              }
              
              // Wait before retry
              if (attempt < 2) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            }

            // Use selected avatar from signup, or existing, or random as fallback
            const avatarUrl = signupData.avatarUrl || existingPrimary?.avatar_url || await getRandomAvatar();

            const profilePayload = {
              name: fullName || signupData.firstName,
              first_name: signupData.firstName,
              last_name: signupData.lastName,
              country: signupData.country,
              avatar_url: avatarUrl,
              updated_at: new Date().toISOString(),
            };

            let primaryProfileId: string | null = existingPrimary?.id ?? null;

            if (primaryProfileId) {
              // Update existing profile created by trigger
              const { error: updateError } = await supabase
                .from('user_profiles')
                .update(profilePayload)
                .eq('id', primaryProfileId)
                .eq('user_id', data.user.id);

              if (updateError) {
                console.error('[AuthModal] Error updating primary profile:', updateError);
              } else {
                console.log('[AuthModal] Profile updated successfully:', { profilePayload, primaryProfileId });
              }
            } else {
              // Fallback: insert if trigger didn't create one
              const { data: inserted, error: insertError } = await supabase
                .from('user_profiles')
                .insert({
                  user_id: data.user.id,
                  ...profilePayload,
                  is_primary: true,
                  profile_type: 'primary',
                  display_order: 0,
                })
                .select('id, name, avatar_url')
                .single();

              if (insertError) {
                console.error('[AuthModal] Error creating primary profile:', insertError);
              } else {
                console.log('[AuthModal] Profile created successfully:', inserted);
              }

              primaryProfileId = inserted?.id ?? null;
            }

            // Preload + snapshot for instant navbar/avatar (prevents grey placeholder)
            if (avatarUrl) {
              preloadAvatar(avatarUrl).catch(() => {});
            }
            if (primaryProfileId) {
              saveProfileSnapshot({
                id: primaryProfileId,
                name: fullName || signupData.firstName,
                avatar_url: avatarUrl,
                profile_type: 'adult',
              });
            }

            // Invalidate the profiles cache so the new profile appears immediately
            await queryClient.invalidateQueries({ queryKey: ['userProfiles', data.user.id] });
          } catch (profileCreateError) {
            console.error('[AuthModal] Error in profile creation:', profileCreateError);
          }
        }

        // Check if user has existing profiles by querying the database
        try {
          const { data: profiles, error: profilesError } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('user_id', data.user?.id);

          if (profilesError) {
            console.error('Error checking user profiles:', profilesError);
            // Fallback to profile selection on error
            if (onTransitionToProfileSelection) {
              onTransitionToProfileSelection();
            } else {
              onClose();
              onAuthSuccess?.();
            }
            return;
          }

          const hasProfiles = profiles && profiles.length > 0;
          const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');

          if (!hasProfiles) {
            // New user without profile - this shouldn't happen now but handle it anyway
            toast({
              title: "Welcome!",
              description: "Let's set up your profile."
            });
            if (onTransitionToProfileSelection) {
              onTransitionToProfileSelection();
            } else {
              onClose();
              onAuthSuccess?.();
            }
          } else if (profileSelectionShown !== 'true') {
            // User has profile(s) but hasn't selected one this session yet
            toast({
              title: "Almost there",
              description: "Choose who’s listening first."
            });
            if (onTransitionToProfileSelection) {
              onTransitionToProfileSelection();
            } else {
              onClose();
              onAuthSuccess?.();
            }
          } else {
            // Existing user who has already been through profile selection
            toast({
              title: "Welcome back!",
              description: "Successfully signed in."
            });
            onClose();
            onAuthSuccess?.();
          }
        } catch (error) {
          console.error('Error in user profile check:', error);
          // Fallback to profile selection on any error
          if (onTransitionToProfileSelection) {
            onTransitionToProfileSelection();
          } else {
            onClose();
            onAuthSuccess?.();
          }
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to verify code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (formData: SignupFormData) => {
    // Store all signup data for profile creation after OTP verification
    setSignupData({
      firstName: formData.firstName,
      lastName: formData.lastName,
      country: formData.country
    });
    
    // Go to avatar selection step instead of directly to OTP
    setStep('avatar');
    sessionStorage.setItem('auth_modal_step', 'avatar');
  };

  const handleAvatarContinue = async () => {
    if (!selectedAvatarUrl || !signupData) return;
    
    // CRITICAL: Unlock media on this user gesture for iOS audio playback
    MediaUnlocker.instance().unlock().catch(() => {});
    
    setIsLoading(true);
    
    // Create updated signup data with avatar - use local variable to avoid race condition
    const updatedSignupData = { ...signupData, avatarUrl: selectedAvatarUrl };
    setSignupData(updatedSignupData);
    
    try {
      // Attempt OTP signup with metadata
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          data: {
            first_name: updatedSignupData.firstName,
            last_name: updatedSignupData.lastName,
            country: updatedSignupData.country,
            full_name: `${updatedSignupData.firstName} ${updatedSignupData.lastName}`
          }
        }
      });

      if (otpError) {
        const msg = String(otpError.message || '').toLowerCase();
        const otpSignupDisabled = msg.includes('signups not allowed for otp') || msg.includes('otp_disabled');
        if (otpSignupDisabled) {
          toast({
            title: 'Signups via OTP are disabled',
            description: 'Please enable Email OTP signups in Supabase Auth settings to allow code-based signup, or enable magic links.',
            variant: 'destructive'
          });
          return;
        }
        throw otpError;
      }

      // OTP was sent successfully
      setStep('otp');
      sessionStorage.setItem('auth_modal_step', 'otp');
      setResendCooldown(60);
      toast({
        title: 'Check your email',
        description: "We've sent a 6-digit code to your email address."
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to create account. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'otp') {
      setStep(isNewUser ? 'avatar' : 'email');
      sessionStorage.setItem('auth_modal_step', isNewUser ? 'avatar' : 'email');
      setOtp('');
      sessionStorage.removeItem('auth_modal_otp');
    } else if (step === 'avatar') {
      setStep('signup');
      sessionStorage.setItem('auth_modal_step', 'signup');
    } else if (step === 'signup') {
      setStep('email');
      sessionStorage.setItem('auth_modal_step', 'email');
      setIsNewUser(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[100vw] h-[100vh] sm:w-[90vw] sm:h-auto sm:max-w-md md:max-w-2xl mx-auto glass-morphism border-0 sm:border border-border/50 shadow-2xl p-0 overflow-hidden z-[99999] sm:rounded-lg rounded-none" onPointerDownOutside={onClose}>
        <div className="relative">
          {/* Floating close button - top right */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute top-12 right-4 z-10 w-10 h-10 p-0 rounded-full bg-white/10 backdrop-blur-xl border-0 hover:bg-white/20 transition-all duration-200 hover:scale-105"
          >
            <X className="w-4 h-4 text-foreground" />
          </Button>
          
          <div id="sign-in-popup" className="relative px-4 sm:px-6 py-12 sm:py-16 flex flex-col items-center justify-center min-h-full sm:min-h-auto w-full max-w-full overflow-hidden">
            <AnimatePresence mode="wait">
              {step === 'email' ? (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6 w-full max-w-md"
                >
                  <div className="text-center space-y-4">
                    <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
                      Continue With Email
                    </h2>
                    <p className="text-foreground/80 mt-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
                      You can sign in if you already have an account, or we'll help you create one.
                    </p>
                  </div>

                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-12 bg-white/10 border-0 backdrop-blur-xl focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors text-base text-foreground placeholder:text-foreground/60"
                        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}
                        disabled={isLoading}
                        autoFocus
                      />
                    </div>

                    {/* Peer to Peer Icon */}
                    <div className="flex justify-center mt-6 mb-3">
                      <img 
                        src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/Icons%20custom/handshake_glass_icon_light.webp" 
                        alt="Handshake" 
                        className="h-10 w-10"
                      />
                    </div>

                    {/* Legal Text */}
                    <p className="text-center text-xs text-foreground/70 mt-3 leading-relaxed px-4 py-3 rounded-lg backdrop-blur-sm">
                      Your email address is used to securely send you a verification code. We do not store passwords or any sensitive data online to protect your account. By continuing, you agree to our Terms of Service and Privacy Policy.
                    </p>

                    <div className="flex justify-center">
                      <Button
                        type="submit"
                        className="w-3/4 h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-base shadow-lg"
                        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}
                        disabled={isLoading || !email.trim()}
                      >
                        {isLoading ? "Processing..." : "Continue"}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              ) : step === 'signup' ? (
                <motion.div
                  key="signup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="w-full max-w-md"
                >
                  <SignupForm
                    email={email}
                    onSubmit={handleSignupSubmit}
                    onBack={handleBack}
                    isLoading={isLoading}
                  />
                </motion.div>
              ) : step === 'avatar' ? (
                <motion.div
                  key="avatar"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="w-full max-w-lg mx-auto flex flex-col space-y-6"
                >
                  {/* Back button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                    className="absolute top-4 left-4 z-10 w-10 h-10 p-0 rounded-full bg-white/10 backdrop-blur-xl border-0 hover:bg-white/20 transition-all duration-200"
                  >
                    <ArrowLeft className="w-4 h-4 text-foreground" />
                  </Button>

                  {/* Title - Always centered */}
                  <div className="text-center space-y-2 pt-4">
                    <h2 
                      className="text-2xl md:text-3xl font-semibold text-foreground tracking-tight"
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}
                    >
                      Choose your look
                    </h2>
                    <p className="text-foreground/60 text-sm">
                      Pick a profile picture
                    </p>
                  </div>

                  {/* Avatar Selection */}
                  {avatarsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Mobile/Tablet: Horizontal scroll */}
                      <div 
                        className="md:hidden w-screen -mx-4 overflow-x-auto py-4 scrollbar-hide"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                      >
                        <div className="flex gap-4 px-8">
                          {displayAvatars.map((avatar, index) => (
                            <motion.button
                              key={avatar.id}
                              onClick={() => handleAvatarSelect(index, avatar.image_url)}
                              whileTap={{ scale: 0.95 }}
                              className={`
                                relative flex-shrink-0 w-20 h-20 rounded-full overflow-hidden
                                transition-all duration-300 ease-out
                                ${selectedAvatarIndex === index 
                                  ? 'ring-4 ring-amber-500 scale-110 shadow-[0_0_20px_rgba(245,158,11,0.4)]' 
                                  : 'ring-2 ring-white/20 hover:ring-white/40'
                                }
                              `}
                            >
                              <img
                                src={avatar.image_url}
                                alt={`Avatar ${index + 1}`}
                                className="w-full h-full object-cover"
                                loading={index < 8 ? 'eager' : 'lazy'}
                              />
                              {selectedAvatarIndex === index && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="absolute inset-0 bg-amber-500/10 pointer-events-none"
                                />
                              )}
                            </motion.button>
                          ))}
                        </div>
                      </div>

                      {/* Desktop: Grid layout */}
                      <div className="hidden md:block max-h-[45vh] overflow-y-auto px-4 scrollbar-hide">
                        <div className="grid grid-cols-4 gap-4 justify-items-center">
                          {displayAvatars.map((avatar, index) => (
                            <motion.button
                              key={avatar.id}
                              onClick={() => handleAvatarSelect(index, avatar.image_url)}
                              whileTap={{ scale: 0.95 }}
                              className={`
                                relative w-20 h-20 rounded-full overflow-hidden
                                transition-all duration-300 ease-out
                                ${selectedAvatarIndex === index 
                                  ? 'ring-4 ring-amber-500 scale-110 shadow-[0_0_20px_rgba(245,158,11,0.4)]' 
                                  : 'ring-2 ring-white/20 hover:ring-white/40'
                                }
                              `}
                            >
                              <img
                                src={avatar.image_url}
                                alt={`Avatar ${index + 1}`}
                                className="w-full h-full object-cover"
                                loading={index < 8 ? 'eager' : 'lazy'}
                              />
                              {selectedAvatarIndex === index && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="absolute inset-0 bg-amber-500/10 pointer-events-none"
                                />
                              )}
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Continue Button */}
                  <div className="flex justify-center pt-4 px-4">
                    <Button
                      onClick={handleAvatarContinue}
                      disabled={!selectedAvatarUrl || isLoading}
                      className="w-full max-w-xs h-14 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg rounded-full shadow-lg disabled:opacity-30"
                    >
                      {isLoading ? "Creating account..." : "Continue"}
                      {!isLoading && <ChevronRight className="ml-2 w-5 h-5" />}
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6 w-full max-w-md"
                >
                  <div className="text-center mb-6">
                    <h2 className="text-3xl font-semibold text-foreground tracking-tight" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>Enter Verification Code</h2>
                  </div>

                  <div className="text-center space-y-3">
                    <p className="text-white">
                      We've sent a 6-digit code to <br />
                      <span className="font-medium text-white">{email}</span>
                    </p>
                    <p className="text-xs text-white">
                      Don't see it? Please check your spam folder.
                    </p>
                  </div>

                  <form onSubmit={handleOtpSubmit} className="space-y-6">
                    <div className="flex justify-center w-full">
                      <InputOTP
                        ref={otpInputRef}
                        value={otp}
                        onChange={setOtp}
                        maxLength={6}
                        disabled={isLoading}
                        className="gap-2"
                        autoFocus
                      >
                        <InputOTPGroup className="gap-3">
                          <InputOTPSlot index={0} className="w-12 h-12 text-lg !border-0 text-white bg-white/10 backdrop-blur-xl rounded-full first:!rounded-full last:!rounded-full shadow-sm focus-within:ring-0 focus-within:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                          <InputOTPSlot index={1} className="w-12 h-12 text-lg !border-0 text-white bg-white/10 backdrop-blur-xl rounded-full first:!rounded-full last:!rounded-full shadow-sm focus-within:ring-0 focus-within:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                          <InputOTPSlot index={2} className="w-12 h-12 text-lg !border-0 text-white bg-white/10 backdrop-blur-xl rounded-full first:!rounded-full last:!rounded-full shadow-sm focus-within:ring-0 focus-within:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                          <InputOTPSlot index={3} className="w-12 h-12 text-lg !border-0 text-white bg-white/10 backdrop-blur-xl rounded-full first:!rounded-full last:!rounded-full shadow-sm focus-within:ring-0 focus-within:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                          <InputOTPSlot index={4} className="w-12 h-12 text-lg !border-0 text-white bg-white/10 backdrop-blur-xl rounded-full first:!rounded-full last:!rounded-full shadow-sm focus-within:ring-0 focus-within:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                          <InputOTPSlot index={5} className="w-12 h-12 text-lg !border-0 text-white bg-white/10 backdrop-blur-xl rounded-full first:!rounded-full last:!rounded-full shadow-sm focus-within:ring-0 focus-within:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    
                    <div className="flex justify-center w-full">
                      <Button 
                        type="submit" 
                        className="h-12 px-6 text-base font-medium w-3/4 rounded-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80"
                        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}
                        disabled={isLoading || otp.length !== 6}
                      >
                        {isLoading ? "Verifying..." : "Sign In"}
                      </Button>
                    </div>
                  </form>

                  <div className="text-center">
                    <button
                      onClick={async () => {
                        if (resendCooldown > 0) return;
                        setIsLoading(true);
                        try {
                          await supabase.auth.signInWithOtp({
                            email: email.trim(),
                            options: { shouldCreateUser: true }
                          });
                          toast({
                            title: "Code Sent",
                            description: "A new verification code has been sent to your email."
                          });
                          setOtp("");
                          setResendCooldown(60);
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to resend code. Please try again.",
                            variant: "destructive"
                          });
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading || resendCooldown > 0}
                      className="text-sm text-white hover:text-white/80 underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Code"}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}